/**
 * Team Management Hooks
 * For org-level admin team member management
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  TeamMember,
  InviteTeamMember,
  Invitation,
  TeamEmailAnalytics,
} from "@property-reports/api-contract";

const API_BASE = import.meta.env.VITE_API_URL || "";

// ============================================================================
// API Functions
// ============================================================================

async function fetchTeamMembers(): Promise<TeamMember[]> {
  const response = await fetch(`${API_BASE}/api/team`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch team members");
  }

  return response.json();
}

async function inviteTeamMember(
  data: InviteTeamMember,
): Promise<{ success: boolean; invitationId: string }> {
  const response = await fetch(`${API_BASE}/api/team/invite`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to invite team member");
  }

  return response.json();
}

async function updateMemberRole(
  memberId: string,
  role: "admin" | "member",
): Promise<TeamMember> {
  const response = await fetch(`${API_BASE}/api/team/${memberId}/role`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to update role");
  }

  return response.json();
}

async function removeMember(memberId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/team/${memberId}`, {
    method: "DELETE",
    credentials: "include",
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to remove member");
  }
}

async function fetchTeamInvitations(): Promise<Invitation[]> {
  const response = await fetch(`${API_BASE}/api/team/invitations`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch invitations");
  }

  return response.json();
}

async function resendTeamInvitation(
  invitationId: string,
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${API_BASE}/api/team/invitations/${invitationId}/resend`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
      credentials: "include",
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to resend invitation");
  }

  return response.json();
}

async function cancelTeamInvitation(
  invitationId: string,
): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(
    `${API_BASE}/api/team/invitations/${invitationId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Failed to cancel invitation");
  }

  return response.json();
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch team members for current organization
 */
export function useTeamMembers() {
  return useQuery({
    queryKey: ["team", "members"],
    queryFn: fetchTeamMembers,
  });
}

/**
 * Hook to invite a new team member
 */
export function useInviteTeamMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: inviteTeamMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team"] });
    },
  });
}

/**
 * Hook to update a team member's role
 */
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      memberId,
      role,
    }: {
      memberId: string;
      role: "admin" | "member";
    }) => updateMemberRole(memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "members"] });
    },
  });
}

/**
 * Hook to remove a team member
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "members"] });
    },
  });
}

/**
 * Hook to fetch team invitations for current organization
 */
export function useTeamInvitations() {
  return useQuery({
    queryKey: ["team", "invitations"],
    queryFn: fetchTeamInvitations,
  });
}

/**
 * Hook to resend a team invitation
 */
export function useResendTeamInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendTeamInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "invitations"] });
    },
  });
}

/**
 * Hook to cancel a team invitation
 */
export function useCancelTeamInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelTeamInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team", "invitations"] });
    },
  });
}

// ============================================================================
// Team Analytics
// ============================================================================

async function fetchTeamAnalytics(days: number): Promise<TeamEmailAnalytics> {
  const response = await fetch(`${API_BASE}/api/team/analytics?days=${days}`, {
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error("Failed to fetch team analytics");
  }

  return response.json();
}

/**
 * Hook to fetch email analytics for team members
 */
export function useTeamAnalytics(days: number = 30) {
  return useQuery({
    queryKey: ["team", "analytics", days],
    queryFn: () => fetchTeamAnalytics(days),
  });
}
